// ─────────────────────────────────────────────────────────────────────────
// LanguageContext.tsx
// Replaces the original toggleLanguage() + .lang-text/data-th/data-en
// DOM-walking approach with a React context. Every entry below was pulled
// directly from a data-th/data-en pair in online_job_sheet.html — the key
// names describe the UI location so usage sites stay self-documenting.
// ─────────────────────────────────────────────────────────────────────────

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Lang = 'th' | 'en';

export const dictionary = {
  // ── Header ──
  pageTitle: { th: 'ใบงานออนไลน์', en: 'Online Job Sheet' },
  assignedChauffeur: { th: 'พนักงานขับรถผู้รับผิดชอบ', en: 'Assigned Chauffeur' },
  jobIdLabel: { th: 'รหัสงาน:', en: 'Job ID:' },

  // ── Status badge ──
  statusReady: { th: 'รอเริ่มงาน', en: 'READY TO START' },
  statusInProgress: { th: 'กำลังดำเนินการ', en: 'IN PROGRESS' },
  statusCompleted: { th: 'เสร็จสิ้น', en: 'COMPLETED' },

  // ── Customer info card ──
  customerInfoTitle: { th: 'ข้อมูลลูกค้า', en: 'Customer Information' },
  customerName: { th: 'ชื่อลูกค้า', en: 'Customer Name' },

  // ── Travel info card ──
  travelInfoTitle: { th: 'ข้อมูลการเดินทาง', en: 'Travel Information' },
  bookingDate: { th: 'วันที่จอง', en: 'Booking Date' },
  serviceDate: { th: 'วันที่ใช้งาน', en: 'Service Date' },
  startTime: { th: 'เวลาเริ่มงาน', en: 'Start Time' },
  endTime: { th: 'เวลาจบงาน', en: 'End Time' },
  flightNo: { th: 'เลขไฟล์ท (Flight No.)', en: 'Flight No.' },
  itinerary: { th: 'รายละเอียดการเดินทาง', en: 'Itinerary' },
  stopPickup: { th: 'จุดรับ', en: 'Pick-up' },
  stopFinal: { th: 'จุดหมายปลายทาง', en: 'Final Drop-off' },
  stopMid: { th: 'จุดแวะ', en: 'Stop' },

  // ── Vehicle & contact card ──
  vehicleInfoTitle: { th: 'ข้อมูลยานพาหนะและการติดต่อ', en: 'Vehicle & Contact Info' },
  driverName: { th: 'ชื่อคนขับ', en: 'Driver Name' },
  driverPhone: { th: 'เบอร์โทรคนขับ', en: 'Driver Phone' },
  plate: { th: 'เลขทะเบียนรถ', en: 'Vehicle No.' },

  // ── Payment slip section ──
  paymentSlipTitle: { th: 'แนบสลิปชำระเงิน', en: 'Attach Payment Slip' },
  paymentRequiredBefore: { th: 'จำเป็นต้องทำก่อนเริ่มงาน', en: 'Required before starting the job' },
  paymentUnpaidBadge: { th: '⚠ ยังไม่ครบ', en: '⚠ UNPAID' },
  paymentApprovedBadge: { th: '✓ ยืนยันแล้ว', en: '✓ APPROVED' },
  paymentNotice: {
    th: 'ลูกค้ายังชำระเงินไม่ครบตามที่บันทึกในระบบ กรุณาแนบสลิปโอนเงินส่วนที่ค้างชำระ แล้วกด "ส่งให้แอดมิน" — ปุ่มเริ่มงานจะเปิดใช้งานได้หลังแอดมินยืนยัน',
    en: 'Customer has not fully paid as recorded. Please attach a transfer slip for the remaining balance and tap Send — the Start button unlocks after admin confirms.',
  },
  amountTotal: { th: 'ยอดรวม', en: 'Total' },
  amountPaid: { th: 'ชำระแล้ว', en: 'Paid' },
  amountRemaining: { th: 'ค้างชำระ', en: 'Remaining' },
  tapToSelectSlip: { th: 'แตะเพื่อเลือกสลิปโอนเงิน', en: 'Tap to select payment slip' },
  slipFormatHint: { th: 'รองรับ JPG / PNG ขนาดไม่เกิน 5MB', en: 'JPG / PNG, max 5MB' },
  tapToTakeSlipPhoto: {
    th: 'แตะเพื่อถ่ายรูปสลิปโอนเงิน (ต้องถ่ายเท่านั้น ห้ามแนบไฟล์)',
    en: 'Tap to take a photo of the payment slip (camera only, no attaching files)',
  },
  slipCameraSizeHint: {
    th: 'ไม่สามารถเลือกรูปจากคลังภาพได้ — ขนาดไม่เกิน 5MB',
    en: 'Cannot choose photos from the gallery — up to 5MB',
  },
  verifyBeforeSend: { th: 'ตรวจสอบสลิปให้ถูกต้องก่อนกดส่ง', en: 'Verify before sending' },
  sendSlipToAdmin: { th: 'ส่งสลิปให้แอดมิน', en: 'Send Slip to Admin' },
  slipSentWaiting: { th: 'ส่งสลิปแล้ว — รอแอดมินยืนยัน', en: 'Slip Sent — Awaiting Confirmation' },
  pendingBadge: { th: 'รอยืนยัน', en: 'PENDING' },
  autoUpdateHint: {
    th: 'หน้านี้จะอัปเดตอัตโนมัติเมื่อแอดมินยืนยัน',
    en: 'Page will update automatically when admin confirms',
  },
  slipApprovedTitle: { th: 'แอดมินยืนยันแล้ว ✓', en: 'Admin Approved ✓' },
  slipApprovedSubtitle: { th: 'สามารถกดปุ่มเริ่มงานได้แล้ว', en: 'You may now start the job' },
  slipSentAtPrefix: { th: 'ส่งเมื่อ: ', en: 'Sent at: ' },

  // ── Audit log ──
  auditLogTitle: { th: 'บันทึกพิกัดและเวลา (หลักฐานความปลอดภัย)', en: 'GPS & Timestamp Audit Log' },
  startJobLog: { th: 'บันทึกเวลาเริ่มงาน', en: 'Start Job Log' },
  awaitingStart: { th: 'รอการกดเริ่มงาน...', en: 'Awaiting job start...' },
  endJobLog: { th: 'บันทึกเวลาจบงาน', en: 'End Job Log' },
  awaitingEnd: { th: 'รอการกดจบงาน...', en: 'Awaiting job completion...' },
  mapLink: { th: 'แผนที่', en: 'Map' },
  startJobPhotoLabel: { th: 'รูปยืนยันตำแหน่งเริ่มงาน', en: 'Start-job location photo' },
  accuracyGood: { th: 'แม่นยำสูง', en: 'High accuracy' },
  accuracyOk: { th: 'แม่นยำปานกลาง', en: 'Moderate accuracy' },
  accuracyPoor: { th: 'แม่นยำต่ำ (อาจมาจากคอมพิวเตอร์/Wi-Fi)', en: 'Low accuracy (may be computer/Wi-Fi based)' },
  metersAbbrev: { th: 'ม.', en: 'm' },

  // ── Success screen ──
  jobCompletedTitle: { th: 'เสร็จสิ้นงานเรียบร้อย', en: 'Job Completed Successfully' },
  jobCompletedSubtitle: {
    th: 'บันทึกข้อมูลการบริการเรียบร้อยตามมาตรฐานเรียลไทม์',
    en: 'Service data successfully logged to system in real-time.',
  },
  completionTimestamp: { th: 'วันเวลาที่บันทึกสำเร็จ', en: 'Completion Timestamp' },

  // ── Delivery photo ──
  deliveryPhotoTitle: { th: 'รูปภาพยืนยันการส่ง', en: 'Delivery Confirmation Photo' },
  deliveryPhotoModalTitle: { th: 'ถ่ายรูปยืนยันการส่ง', en: 'Delivery Confirmation Photo' },
  deliveryPhotoModalSubtitle: {
    th: 'กรุณาถ่ายรูปก่อนกดจบงาน',
    en: 'Please take a photo before completing the job',
  },
  tapToTakePhoto: {
    th: 'แตะเพื่อถ่ายรูป (ต้องถ่ายเท่านั้น ห้ามแนบไฟล์)',
    en: 'Tap to take a photo (camera only, no attaching files)',
  },
  photoSizeHint: {
    th: 'ไม่สามารถเลือกรูปจากคลังภาพได้ — ขนาดไม่เกิน 10MB',
    en: 'Cannot choose photos from the gallery — up to 10MB',
  },
  retake: { th: 'ถ่ายใหม่', en: 'Retake' },
  nextSignature: { th: 'ถัดไป: เซ็นชื่อลูกค้า', en: 'Next: Customer Signature' },
  takenAt: { th: 'ถ่ายเมื่อ', en: 'Taken at' },

  // ── Start job photo (verify presence before starting) ──
  startJobPhotoModalTitle: { th: 'ถ่ายรูปยืนยันตำแหน่งก่อนเริ่มงาน', en: 'Confirm Location Before Starting' },
  startJobPhotoModalSubtitle: {
    th: 'กรุณาถ่ายรูปสถานที่จริงก่อนกดเริ่มงาน',
    en: 'Please take a photo of the actual location before starting the job',
  },
  tapToTakeStartPhoto: {
    th: 'แตะเพื่อถ่ายรูปยืนยันตำแหน่ง (ต้องถ่ายเท่านั้น ห้ามแนบไฟล์)',
    en: 'Tap to take a location photo (camera only, no attaching files)',
  },
  confirmStartJob: { th: 'ยืนยันเริ่มงาน', en: 'Confirm & Start Job' },

  // ── Signature ──
  signatureSectionTitle: { th: 'ลายเซ็นลูกค้ายืนยันการรับบริการ', en: 'Customer Signature Confirmation' },
  signaturePermanentNote: {
    th: 'ลายเซ็นดิจิทัล — บันทึกถาวร ไม่สามารถแก้ไขได้',
    en: 'Digital Signature — Permanently recorded, cannot be modified',
  },
  signatureModalTitle: { th: 'ลายเซ็นลูกค้า', en: 'Customer Signature' },
  signatureModalSubtitle: {
    th: 'กรุณาเซ็นเพื่อยืนยันการรับบริการ',
    en: 'Please sign to confirm service completion',
  },
  signHere: { th: 'กรุณาเซ็นชื่อภายในพื้นที่ด้านล่าง', en: 'Please sign inside the area below' },
  clearSignature: { th: 'ล้างลายเซ็น', en: 'Clear Signature' },
  saveAndComplete: { th: 'บันทึกและจบงาน', en: 'Save & Complete Job' },

  // ── Action bar ──
  startJob: { th: 'เริ่มงาน', en: 'Start Job' },
  completeJob: { th: 'กดจบงาน', en: 'Complete Job' },
  fetchingGps: { th: 'กำลังดึงพิกัด...', en: 'Fetching GPS...' },

  // ── Location gate (must grant real GPS before starting the job) ──
  locationRequiredTitle: { th: 'ต้องเปิดใช้งานตำแหน่ง (GPS)', en: 'Location (GPS) Required' },
  locationRequiredSubtitle: {
    th: 'ต้องยืนยันตำแหน่งจริงของคนขับก่อนจึงจะเริ่มงานได้',
    en: 'The driver\u2019s real position must be confirmed before starting the job',
  },
  locationWaitingFix: { th: 'กำลังค้นหาสัญญาณ GPS...', en: 'Acquiring GPS signal...' },
  locationDeniedTitle: { th: 'คุณปฏิเสธการเข้าถึงตำแหน่ง', en: 'Location Access Denied' },
  locationDeniedBody: {
    th: 'กรุณาเปิดสิทธิ์การเข้าถึงตำแหน่งของเบราว์เซอร์สำหรับหน้านี้ แล้วโหลดหน้าใหม่อีกครั้ง',
    en: 'Please enable location permission for this page in your browser settings, then reload the page.',
  },
  locationUnsupportedTitle: { th: 'อุปกรณ์นี้ไม่รองรับ GPS', en: 'Device Does Not Support GPS' },
  locationUnsupportedBody: {
    th: 'กรุณาเปิดลิงก์นี้จากโทรศัพท์หรือเบราว์เซอร์อื่นที่รองรับตำแหน่ง',
    en: 'Please open this link on a phone or browser that supports location.',
  },
  waitingGpsButtonLabel: { th: 'รอสัญญาณ GPS...', en: 'Waiting for GPS...' },
  submittedSuccessfully: { th: 'ส่งข้อมูลสำเร็จ', en: 'Submitted Successfully' },

  // ── Misc / alerts ──
  photoExceeds10mb: {
    th: 'รูปภาพขนาดใหญ่เกิน 10MB กรุณาเลือกรูปที่เล็กกว่า',
    en: 'Photo exceeds 10MB, please choose a smaller file',
  },
  slipExceeds5mb: {
    th: 'ไฟล์ใหญ่เกิน 5MB กรุณาเลือกใหม่',
    en: 'File exceeds 5MB. Please choose another.',
  },

  // ── In-app browser (LINE/Facebook/Instagram) camera warning ──
  inAppBrowserTitle: {
    th: 'กล้องอาจใช้งานไม่ได้ในหน้านี้',
    en: 'Camera may not work in this view',
  },
  inAppBrowserBodyLine: {
    th: 'คุณเปิดลิงก์นี้ผ่านแอป LINE ซึ่งบางครั้งไม่รองรับการเปิดกล้องถ่ายรูปสด ทำให้ระบบขึ้นหน้าต่างแนบไฟล์แทน กรุณากดปุ่มด้านล่างเพื่อเปิดในเบราว์เซอร์จริง',
    en: 'You opened this link inside the LINE app, which sometimes can\u2019t access the live camera — the system falls back to a plain file-attach dialog. Tap the button below to open this in your real browser.',
  },
  inAppBrowserBodyGeneric: {
    th: 'คุณเปิดลิงก์นี้ผ่านเบราว์เซอร์ในแอป ซึ่งบางครั้งไม่รองรับการเปิดกล้องถ่ายรูปสด ทำให้ระบบขึ้นหน้าต่างแนบไฟล์แทน แนะนำให้เปิดลิงก์นี้ในเบราว์เซอร์จริงแทน',
    en: 'You opened this link inside an app\u2019s browser, which sometimes can\u2019t access the live camera — the system falls back to a plain file-attach dialog. We recommend opening this link in your real browser instead.',
  },
  openInBrowserButton: {
    th: 'เปิดในเบราว์เซอร์',
    en: 'Open in Browser',
  },
  inAppBrowserManualLine: {
    th: 'ถ้ากดแล้วไม่เปลี่ยนหน้า: แตะปุ่ม ⋯ มุมขวาบน แล้วเลือก "เปิดใน Safari/Chrome"',
    en: 'If tapping the button doesn\u2019t redirect you: tap the ⋯ menu at the top-right, then choose "Open in Safari/Chrome".',
  },
  inAppBrowserManualGeneric: {
    th: 'วิธีแก้: แตะปุ่ม ⋯ หรือไอคอนแชร์ มุมขวาบน แล้วเลือก "เปิดในเบราว์เซอร์"',
    en: 'To fix: tap the ⋯ or share icon at the top-right, then choose "Open in Browser".',
  },
} as const;

export type DictionaryKey = keyof typeof dictionary;

interface LanguageContextValue {
  lang: Lang;
  toggleLanguage: () => void;
  /** t('startJob') -> 'เริ่มงาน' | 'Start Job' depending on current lang */
  t: (key: DictionaryKey) => string;
  /** Locale string for Date.toLocaleString, matching the original's th-TH/en-US split */
  locale: 'th-TH' | 'en-US';
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>('th');

  const toggleLanguage = useCallback(() => {
    setLang((prev) => (prev === 'th' ? 'en' : 'th'));
  }, []);

  const t = useCallback((key: DictionaryKey) => dictionary[key][lang], [lang]);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, toggleLanguage, t, locale: lang === 'th' ? 'th-TH' : 'en-US' }),
    [lang, toggleLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}

/** Formats a Date the same way the original did: toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) */
export function formatDateTime(date: Date, locale: 'th-TH' | 'en-US'): string {
  return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}
